import React, { useCallback } from 'react'
import { getCookie } from 'cookies-next'
import { useAnalytics } from 'src/common/analytics'
import { useLeadId } from 'src/common/lead-id'
import { SCHEDULE_CALL_URL } from 'src/common/constants'
import { cn } from 'src/utils/cn'
import { waitForTracking } from 'src/common/analytics/helpers'

type ScheduleACallCTAVariant = 'contained' | 'outlined'

const variantStyles: Record<ScheduleACallCTAVariant, string> = {
  contained: 'border-2 border-blue-accent bg-blue-accent text-white',
  outlined: 'border-2 border-blue-base bg-white text-blue-base',
}

const baseStyles = 'whitespace-nowrap rounded-lg px-4 py-2 text-16 font-bold'

type ScheduleACallCTAProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children' | 'onClick'
> & {
  nav_link_section?: string
  page_section?: string
  variant?: ScheduleACallCTAVariant
  children?: string
}

export const ScheduleACallCTA = ({
  nav_link_section = '',
  page_section = '',
  variant = 'contained',
  className,
  children = 'Schedule a call',
  ...props
}: ScheduleACallCTAProps) => {
  const { track } = useAnalytics() as {
    track: (
      data: Record<string, unknown>,
      options: Record<string, unknown>,
      event: string,
      statusCode?: number
    ) => Promise<void> | void
  }

  const leadId = useLeadId()

  const handleScheduleCallClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault?.()
      const url = SCHEDULE_CALL_URL({
        leadId,
        visitId: getCookie('eh_visit_id'),
      })

      const tracking = track(
        {
          click_id: children,
          click_text: `FDR-Web | ${children}`,
          click_type: 'Button Click',
          click_url: url,
          nav_link_section,
          page_section,
          track_event: 'schedule-a-call-cta',
        },
        {},
        'click'
      )

      await waitForTracking(tracking)

      window.location.href = url
    },
    [leadId, track, children, nav_link_section]
  )

  return (
    <button
      data-testid="schedule-a-call-cta"
      type="button"
      onClick={handleScheduleCallClick}
      className={cn(baseStyles, variantStyles[variant], className)}
      {...props}
    >
      {children}
    </button>
  )
}
